// ============================================================
// InviteToast — recipient-side non-blocking invite popup
// ============================================================
//
// Shown at the top-right of a lobby scene when `multiplayer:invite-received`
// arrives. Stacks vertically; caps at MAX_ACTIVE to avoid spam. Each toast:
//   • Renders inviter name + mode label (PvP/Co-op) + 60s countdown
//   • Accept / Decline mini-buttons (calls MultiplayerService.*)
//   • Auto-dismisses at expiry (TTL drops toasts silently — server also
//     emits invite-expired but the client doesn't need to act on it because
//     the toast has already cleaned itself up)
//
// Singleton because: invite-received handlers are scene-agnostic and the
// toast row persists across lobby-scene transitions (cross-mode accept).

import Phaser from 'phaser';
import type { GameMode } from '@roe/shared';
import { MultiplayerService } from '../services/MultiplayerService.js';

const MAX_ACTIVE = 3;
const TOAST_W = 280;
const TOAST_H = 90;
const MARGIN = 16;
const DEPTH = 500;

export interface InvitePayload {
  inviteId: string;
  fromUserId: string;
  fromName: string;
  mode: GameMode;
  expiresAt: number;
}

interface ToastHandle {
  inviteId: string;
  scene: Phaser.Scene;
  objects: Array<{ destroy: () => void }>;
  tick?: { destroy: () => void };
  expiry?: { destroy: () => void };
}

export class InviteToast {
  private static _instance: InviteToast;
  private active: ToastHandle[] = [];
  /** Phaser game reference set once at boot via `setGame`. Used by
   *  `enqueue` to find the currently-active scene at render time so the
   *  toast surfaces regardless of which scene the user is on. Without this,
   *  invites arriving on a scene that didn't call `bindInviteToast`
   *  (HeroScene, MerchantScene, etc.) were silently dropped. */
  private game: Phaser.Game | null = null;
  /** Pending invites awaiting an active scene to render on. Flushed by
   *  `_tryFlush()` whenever `setGame` is called or `enqueue` runs and an
   *  active scene exists. */
  private queue: InvitePayload[] = [];

  static getInstance(): InviteToast {
    if (!InviteToast._instance) InviteToast._instance = new InviteToast();
    return InviteToast._instance;
  }

  /** Wire the Phaser game reference at boot. Call once from BootScene or
   *  the game-create code path. Idempotent. */
  setGame(game: Phaser.Game): void {
    this.game = game;
    this._tryFlush();
  }

  /** Auto-routing entry point used by MultiplayerService when no scene-
   *  level handler is bound. Renders the toast on whichever active scene
   *  exists right now; queues for later if none is active yet. */
  enqueue(invite: InvitePayload): void {
    console.debug('[mp-invite-debug] InviteToast.enqueue', {
      inviteId: invite.inviteId,
      queueLength: this.queue.length + 1,
      gameWired: this.game !== null,
    });
    this.queue.push(invite);
    this._tryFlush();
  }

  /** Drain the queue onto whatever active scene is running. Called from
   *  setGame and enqueue. Safe to call repeatedly — does nothing if no
   *  game is wired or no active scene exists.
   *
   *  Picks the LAST active scene (reverse iteration) so overlay scenes
   *  (HeroScene launched on top of MenuScene, SocialHub modal, PlacementOverlay,
   *  etc.) render the toast on their own display list — sitting above any
   *  dim/interactive layers the overlay installed. Picking the first active
   *  scene would render under the overlay's modal backdrop and the
   *  Accept/Decline buttons would be unreachable. */
  private _tryFlush(): void {
    if (!this.game) {
      console.debug('[mp-invite-debug] InviteToast._tryFlush: no game wired, queueing');
      return;
    }
    if (this.queue.length === 0) return;
    const scenes = this.game.scene?.scenes ?? [];
    let target: Phaser.Scene | undefined;
    for (let i = scenes.length - 1; i >= 0; i--) {
      const s = scenes[i];
      const sys = (s as Phaser.Scene & { sys?: { isActive?: () => boolean } }).sys;
      if (sys && typeof sys.isActive === 'function' && sys.isActive()) {
        target = s;
        break;
      }
    }
    if (!target) {
      console.debug('[mp-invite-debug] InviteToast._tryFlush: no active scene, holding queue', {
        queueLength: this.queue.length,
        sceneCount: scenes.length,
      });
      return;
    }
    console.debug('[mp-invite-debug] InviteToast._tryFlush: draining', {
      queueLength: this.queue.length,
      targetScene: (target as { scene?: { key?: string } }).scene?.key ?? 'unknown',
    });
    while (this.queue.length > 0) {
      const next = this.queue.shift()!;
      this.showInvite(target, next);
    }
  }

  getActiveCount(): number {
    return this.active.length;
  }

  showInvite(scene: Phaser.Scene, invite: InvitePayload): void {
    console.debug('[mp-invite-debug] InviteToast.showInvite', {
      inviteId: invite.inviteId,
      sceneKey: (scene as { scene?: { key?: string } }).scene?.key ?? 'unknown',
      activeCount: this.active.length,
    });
    // Drop zombie handles whose scene transitioned away before their
    // delayedCall fired — without this, a single scene change (e.g. user
    // bounced Menu→Lobby→Menu→Lobby) leaks 1 handle per cycle until
    // active.length reaches MAX_ACTIVE and silently swallows every future
    // invite. Manifests as "I never got the invite toast" in the lobby
    // even though the server emitted invite-received.
    this.active = this.active.filter((h) => {
      if (h.scene === scene) return true;
      const sys = (h.scene as Phaser.Scene & { sys?: { isActive?: () => boolean; isDestroyed?: () => boolean } }).sys;
      const alive = !!sys && (sys.isDestroyed ? !sys.isDestroyed() : (sys.isActive ? sys.isActive() : true));
      if (alive) return true;
      // Orphaned handle — destroy any surviving objects defensively then drop.
      h.tick?.destroy();
      h.expiry?.destroy();
      for (const o of h.objects) {
        try { o.destroy(); } catch { /* already destroyed */ }
      }
      return false;
    });

    if (this.active.length >= MAX_ACTIVE) return; // drop silently

    const idx = this.active.length;
    const x = scene.cameras?.main?.width ? scene.cameras.main.width - MARGIN - TOAST_W / 2 : 960 - MARGIN - TOAST_W / 2;
    const y = MARGIN + TOAST_H / 2 + idx * (TOAST_H + 8);

    const accent = invite.mode === 'pvp' ? 0x8b2000 : 0x5a7030;
    const objects: Array<{ destroy: () => void }> = [];

    const bg = scene.add.rectangle(x, y, TOAST_W, TOAST_H, 0x0d0a0e, 0.97)
      .setStrokeStyle(2, accent).setDepth(DEPTH);
    objects.push(bg);

    const fromTxt = scene.add.text(x - TOAST_W / 2 + 12, y - TOAST_H / 2 + 10, invite.fromName, {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0, 0).setDepth(DEPTH + 1);
    objects.push(fromTxt);

    const modeLabel = invite.mode === 'pvp' ? '⚔ PvP invite' : '🤝 Co-op invite';
    const modeTxt = scene.add.text(x - TOAST_W / 2 + 12, y - TOAST_H / 2 + 30, modeLabel, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6a28',
    }).setOrigin(0, 0).setDepth(DEPTH + 1);
    objects.push(modeTxt);

    const countdownTxt = scene.add.text(x + TOAST_W / 2 - 12, y - TOAST_H / 2 + 10, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#7acc60',
    }).setOrigin(1, 0).setDepth(DEPTH + 1);
    objects.push(countdownTxt);

    const acceptBtn = scene.add.text(x - 50, y + TOAST_H / 2 - 16, 'Accept', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#7acc60', fontStyle: 'bold',
      backgroundColor: '#1a1208', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(DEPTH + 1);
    acceptBtn.on('pointerdown', () => {
      MultiplayerService.getInstance().acceptInvite(invite.inviteId);
    });
    objects.push(acceptBtn);

    const declineBtn = scene.add.text(x + 50, y + TOAST_H / 2 - 16, 'Decline', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#ef4444',
      backgroundColor: '#1a1208', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(DEPTH + 1);
    declineBtn.on('pointerdown', () => {
      MultiplayerService.getInstance().declineInvite(invite.inviteId);
      this.dismissInvite(invite.inviteId);
    });
    objects.push(declineBtn);

    const handle: ToastHandle = { inviteId: invite.inviteId, scene, objects };

    // Countdown tick — update the label once per second until expiry.
    handle.tick = scene.time.addEvent?.({
      delay: 1000,
      loop: true,
      callback: () => {
        const remaining = Math.max(0, Math.ceil((invite.expiresAt - Date.now()) / 1000));
        countdownTxt.setText(`${remaining}s`);
      },
    }) as { destroy: () => void } | undefined;
    // Initial render so "60s" shows immediately.
    const initial = Math.max(0, Math.ceil((invite.expiresAt - Date.now()) / 1000));
    countdownTxt.setText(`${initial}s`);

    // Auto-dismiss at expiry — server also emits invite-expired but we don't
    // need it because the toast handles its own lifetime.
    const ttlMs = Math.max(0, invite.expiresAt - Date.now());
    handle.expiry = scene.time.delayedCall?.(ttlMs, () => {
      this.dismissInvite(invite.inviteId);
    }) as { destroy: () => void } | undefined;

    this.active.push(handle);

    // Belt-and-suspenders: if the scene shuts down before the toast TTL
    // elapses, dismiss the handle proactively. Without this the singleton
    // leaks zombie handles and eventually drops new invites at MAX_ACTIVE.
    scene.events?.once?.('shutdown', () => this.dismissInvite(invite.inviteId));
    scene.events?.once?.('destroy', () => this.dismissInvite(invite.inviteId));
  }

  /** Called from MultiplayerLobbyBase on `multiplayer:invite-error` — replaces
   *  the Accept/Decline row with a short error label, then dismisses after 2s. */
  handleError(inviteId: string, message: string): void {
    const handle = this.active.find((a) => a.inviteId === inviteId);
    if (!handle) return;
    // Destroy the Accept / Decline buttons (last two objects pushed), keep bg + labels.
    for (let i = handle.objects.length - 1; i >= handle.objects.length - 2 && i >= 0; i--) {
      handle.objects[i].destroy();
    }
    // Render the error label + auto-dismiss after 2s.
    const errorLabel = handle.scene.add.text(0, 0, message, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#ef4444', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2);
    handle.objects.push(errorLabel);
    handle.scene.time.delayedCall?.(2000, () => this.dismissInvite(inviteId));
  }

  dismissInvite(inviteId: string): void {
    const idx = this.active.findIndex((a) => a.inviteId === inviteId);
    if (idx < 0) return;
    const handle = this.active[idx];
    handle.tick?.destroy();
    handle.expiry?.destroy();
    for (const o of handle.objects) {
      try { o.destroy(); } catch { /* already destroyed */ }
    }
    this.active.splice(idx, 1);
  }
}
